import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, CircularProgress, Typography, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import config from "./global/config";
import { EditCashPaymentModal, EditInstallmentModal } from "./EditPaymentModal";
import InstallmentDetailsDialog from "./InstallmentDetailsDialog";

const PaymentDetailsGrid = ({ saleId, paymentType, colors }) => {
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedInstallmentId, setSelectedInstallmentId] = useState(null);

  const isInstallmentPayment =
    paymentType?.toLowerCase() === "tranches" ||
    paymentType?.toLowerCase() === "tranche";

  const handleRowDoubleClick = (params) => {
    if (isInstallmentPayment) {
      console.log(
        "Opening details for installment:",
        params.row.id_paiement_tranche
      ); // Debug log
      setSelectedInstallmentId(params.row.id_paiement_tranche);
      setDetailsDialogOpen(true);
    }
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedInstallmentId(null);
  };

  const api = axios.create({
    baseURL: config.apiUrl,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.code === "ERR_NETWORK") {
        console.error("CORS or Network Error:", error);
        setError(
          "Unable to connect to the server. Please check your network connection or contact support."
        );
      } else {
        console.error("API Error:", error);
        setError(error.response?.data?.error || "An unexpected error occurred");
      }
      return Promise.reject(error);
    }
  );

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  const handleEditClick = (payment) => {
    // Deep clone the payment object to avoid reference issues
    const paymentClone = JSON.parse(JSON.stringify(payment));

    // Format dates and numbers before setting the selected payment
    const formattedPayment = {
      ...paymentClone,
      // Convert date strings to Date objects for the modal
      date_paiement: paymentClone.date_paiement
        ? new Date(paymentClone.date_paiement)
        : null,
      date_debut: paymentClone.date_debut
        ? new Date(paymentClone.date_debut)
        : null,
      // Ensure numeric values are properly typed
      montant_a_payer: Number(paymentClone.montant_a_payer || 0),
      montant_paye: Number(paymentClone.montant_paye || 0),
      montant_total: Number(paymentClone.montant_total || 0),
      montant_par_tranche: Number(paymentClone.montant_par_tranche || 0),
      nombre_tranches: Number(paymentClone.nombre_tranches || 0),
      periodicite_jours: Number(paymentClone.periodicite_jours || 0),
    };

    console.log("Opening modal with payment:", formattedPayment); // Debug log
    setSelectedPayment(formattedPayment);
    setEditModalOpen(true);
  };

  const handleSaveEdit = async (updatedPayment) => {
    try {
      const endpoint =
        paymentType === "Cash"
          ? `paiements_clients_cash.php?id_paiement_cash=${updatedPayment.id_paiement_cash}`
          : `paiements_clients_tranches.php?id_paiement_tranche=${updatedPayment.id_paiement_tranche}`;

      // Format dates for API submission
      const formattedPayment = {
        ...updatedPayment,
        date_paiement:
          updatedPayment.date_paiement instanceof Date
            ? updatedPayment.date_paiement.toISOString().split("T")[0]
            : updatedPayment.date_paiement,
        date_debut:
          updatedPayment.date_debut instanceof Date
            ? updatedPayment.date_debut.toISOString().split("T")[0]
            : updatedPayment.date_debut,
      };

      const response = await api.post(endpoint, {
        METHOD: "PUT",
        ...formattedPayment,
        user_upd: localStorage.getItem("userId"),
      });

      if (response.status === 200) {
        await fetchPaymentDetails();
        setEditModalOpen(false);
        setSelectedPayment(null);
      }
    } catch (error) {
      console.error("Error updating payment:", error);
      setError(error.response?.data?.error || "Error updating payment");
    }
  };

  const fetchPaymentDetails = async () => {
    if (!saleId) return;

    setLoading(true);
    setError(null);

    try {
      const endpoint =
        paymentType === "Cash"
          ? `paiements_clients_cash.php?id_vente=${saleId}`
          : `paiements_clients_tranches.php?id_vente=${saleId}`;

      const response = await api.get(endpoint);

      const processedData = Array.isArray(response.data)
        ? response.data.map((row) => ({
            ...row,
            id:
              paymentType === "Cash"
                ? row.id_paiement_cash
                : row.id_paiement_tranche,
            // Format numeric values
            montant_a_payer: Number(row.montant_a_payer || 0),
            montant_paye: Number(row.montant_paye || 0),
            montant_total: Number(row.montant_total || 0),
            montant_par_tranche: Number(row.montant_par_tranche || 0),
            nombre_tranches: Number(row.nombre_tranches || 0),
            periodicite_jours: Number(row.periodicite_jours || 0),
          }))
        : [];

      setPaymentDetails(processedData);
    } catch (error) {
      console.error("Error fetching payment details:", error);
      setError(
        error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produite lors de la récupération des détails de paiement"
      );
    } finally {
      setLoading(false);
    }
  };
  const actionColumn = {
    field: "actions",
    headerName: "Actions",
    width: 100,
    renderCell: (params) => (
      <IconButton
        onClick={() => handleEditClick(params.row)}
        color="primary"
        size="small"
      >
        <EditIcon />
      </IconButton>
    ),
  };

  const cashPaymentColumns = [
    {
      field: "numero_paiement",
      headerName: "N° Paiement",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "date_paiement",
      headerName: "Date",
      flex: 1,
    },
    {
      field: "montant_a_payer",
      headerName: "Montant à Payer",
      flex: 1,
    },
    {
      field: "montant_paye",
      headerName: "Montant Payé",
      flex: 1,
    },
    {
      field: "mode_paiement",
      headerName: "Mode Paiement",
      flex: 1,
    },
    {
      field: "reference_paiement",
      headerName: "Référence",
      flex: 1,
    },
    actionColumn,
  ];

  const installmentColumns = [
    {
      field: "numero_paiement",
      headerName: "N° Paiement",
      flex: 1,
    },
    {
      field: "nombre_tranches",
      headerName: "Nombre de tranches",
      flex: 1,
    },
    {
      field: "montant_total",
      headerName: "Montant Total",
      flex: 1,
      //
    },
    {
      field: "montant_par_tranche",
      headerName: "Montant par tranche",
      flex: 1,
    },
    {
      field: "date_debut",
      headerName: "Date de début",
      flex: 1,
      //
    },
    {
      field: "periodicite_jours",
      headerName: "Périodicité (jours)",
      flex: 1,
    },
    {
      field: "statut_tranches",
      headerName: "Statut",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleEditClick(params.row)}
          color="primary"
          size="small"
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  useEffect(() => {
    fetchPaymentDetails();
  }, [saleId, paymentType]);

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        p={2}
      >
        <Typography color="error" align="center">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      height="75vh"
      sx={{
        "& .MuiDataGrid-root": {
          border: "none",
        },
        "& .MuiDataGrid-cell": {
          borderBottom: "none",
        },
        "& .name-column--cell": {
          color: colors.greenAccent[300],
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: colors.blueAccent[700],
          borderBottom: "none",
        },
        "& .MuiDataGrid-virtualScroller": {
          backgroundColor: colors.secondary,
        },
        "& .MuiDataGrid-footerContainer": {
          borderTop: "none",
          backgroundColor: colors.blueAccent[700],
        },
      }}
    >
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DataGrid
            rows={paymentDetails}
            columns={
              paymentType === "Cash" ? cashPaymentColumns : installmentColumns
            }
            pageSize={10}
            rowsPerPageOptions={[10]}
            disableSelectionOnClick
            onRowDoubleClick={handleRowDoubleClick}
          />

          {selectedPayment &&
            (paymentType === "Cash" ? (
              <EditCashPaymentModal
                open={editModalOpen}
                onClose={() => {
                  setEditModalOpen(false);
                  setSelectedPayment(null);
                }}
                payment={selectedPayment}
                onSave={handleSaveEdit}
              />
            ) : (
              <EditInstallmentModal
                open={editModalOpen}
                onClose={() => {
                  setEditModalOpen(false);
                  setSelectedPayment(null);
                }}
                payment={selectedPayment}
                onSave={handleSaveEdit}
              />
            ))}

          {isInstallmentPayment && (
            <InstallmentDetailsDialog
              open={detailsDialogOpen}
              onClose={handleCloseDetailsDialog}
              installmentId={selectedInstallmentId}
              colors={colors}
              saleId={saleId}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default PaymentDetailsGrid;
